<template>
  <div class="CalculatorBody">
    <v-container id="printPDF">
      <h2>{{ $t("report_title") }}</h2>
      <p>{{ $t("report_text") }}</p>
      <h2>{{ $t("patient_info_title") }}</h2>
      <v-row row>
        <v-col lg4>
          <p>
            <label>
              {{ $t("patient_identificator") }}:
              {{ patient.initials }}
            </label>
          </p>
        </v-col>
        <v-col lg4>
          <p>
            <label>
              {{ $t("birth_date") }}:
              {{ czechFormatDate(patient.birthdate) }}
            </label>
          </p>
        </v-col>
        <v-col lg4>
          <p>
            <label>
              {{ $t("result_date") }}:
              {{ evaluationDateFormatted }}
            </label>
          </p>
        </v-col>
      </v-row>
    </v-container>
    <!-- human body -->
    <v-container>
      <calculatorHumanBody
        id="calculatorHumanBody"
        :validate.sync="validateHumanBody"
        :multiplier="multiplier"
        :editedEvaluation="editedForm"
        evaluating
        @save-patient-click="validateHumanBody = true"
        @patient-body="onSavePatient"
        @generate-report="onGenerateReportClick"
      >
        <template #patient>
          <div>
            <div id="patient-ribbon">
              <h2>
                {{ $t("patient") }}:
                {{ patient.initials }}
              </h2>
              <v-subheader>
                {{ $t("gender") }}: {{ $t(`${patient.gender}_alt`) }}, <br />
                {{ $t("birth") }}: {{ czechFormatDate(patient.birthdate) }},
                <br />
                Datum vyhodnocení: {{ evaluatedDate }}
              </v-subheader>
            </div>
          </div>
        </template>
      </calculatorHumanBody>
    </v-container>
    <v-dialog v-model="showDialog" persistent max-width="400">
      <v-card>
        <v-card-title>{{ $t("evaluation_date") }}</v-card-title>
        <v-form
          id="patientInfoBox"
          ref="evaluationForm"
          v-model="isFormValid"
          @submit.prevent="parseDate(evaluationDateFormatted), onConfirmClick()"
        >
          <div v-click-outside="() => (showDatepicker = false)">
            <v-text-field
              v-model="evaluationDateFormatted"
              :label="$t('result_date')"
              :rules="[rules.required, rules.date]"
              hint="DD. MM. YYYY"
              persistent-hint
              @blur="evaluationDate = parseDate(evaluationDateFormatted)"
              @click="showDatepicker = true"
              outlined
              dense
            >
              <template v-slot:append>
                <div v-if="showDatepicker" class="date-picker-wrapper">
                  <v-card>
                    <v-date-picker
                      v-model="evaluationDate"
                      :allowed-dates="disableFutureDates"
                      no-title
                      @input="onDateSelect"
                    />
                  </v-card>
                </div>
              </template>
            </v-text-field>
          </div>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="gray" text @click="showDialog = false">{{
            $t("cancel")
          }}</v-btn>
          <v-btn
            :loading="loading"
            color="#324c8d"
            text
            @click="onConfirmClick"
          >
            {{
              addEvaluationAction ? $t("save_record") : $t("generate_report")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import ClickOutside from "vue-click-outside";

import calculatorHumanBody from "@/components/Calculator_human_body.vue";
import tokenMixin from "@/mixins/tokenMixin.js";

export default {
  name: "EditEvaluation",
  components: { calculatorHumanBody },
  mixins: [tokenMixin],
  directives: { ClickOutside },
  data() {
    return {
      drawer: null,
      patientName: "",
      patientIdName: "",
      patientGender: "Žena",
      pickedDate: "",
      pickedBirthDate: "",
      language: "Čeština",
      czech: null,
      bodyParts: [],
      loading: false,
      evaluationDateMenu: false,
      evaluationDate: "",
      evaluatedDate: null,
      evaluationDateFormatted: "",
      isFormValid: true,
      showDialog: false,
      showDatepicker: false,
      patient: {
        birthdate: "",
        gender: "",
        initials: ""
      },
      editedForm: null,
      forms: [],
      actionType: "",
      validateHumanBody: false,
      multiplier: [0.1, 0.2, 0.3, 0.4],
      rules: {
        required: value => !!value || this.$t("validate_required"),
        date: v => {
          if (
            /^(0?[1-9]|[12]\d|3[01])[.]\s(0?[1-9]|1[0-2])[.]\s(19|20)\d{2}$/.test(
              v
            )
          ) {
            if (moment(v, "DD. MM. YYYY").isBefore(moment())) {
              return true;
            } else {
              return this.$t("future_date_error");
            }
          } else {
            return this.$t("wrong_date_format");
          }
        }
      }
    };
  },
  computed: {
    patientId() {
      return this.$route.params.id;
    },
    formId() {
      return this.$route.params.formId;
    },
    addEvaluationAction() {
      return this.actionType === "addEvaluation";
    },
    generateReportAction() {
      return this.actionType === "generateReport";
    }
  },
  watch: {
    evaluationDate() {
      this.evaluationDateFormatted = this.formatDate(this.evaluationDate);
    }
  },
  mounted() {
    this.fetchPatient();
  },
  methods: {
    clickToScroll(el, pos) {
      let element = document.querySelector(el);
      element.scrollIntoView({
        behavior: "smooth",
        block: pos,
        inline: "nearest"
      });
      document.getElementById(el).scrollTop += 100;
    },
    onLocaleChange() {
      this.$i18n.locale = this.$i18n.locale === "cs" ? "sk" : "cs";
      this.language = this.czech === true ? "Čeština" : "Slovenština";
    },
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}. ${month}. ${year}`;
    },
    parseDate(date) {
      if (!date) return null;
      const [day, month, year] = date.split(". ");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    onSavePatient(event) {
      this.actionType = "addEvaluation";
      this.showDialog = true;
      this.bodyParts = event;
    },
    czechFormatDate(date) {
      return moment(date).format("DD. MM. YYYY");
    },
    onDateSelect(event) {
      this.showDatepicker = false;
      this.$emit("update:pickedDate", event);
    },
    disableFutureDates(date) {
      return moment(date).isBefore(moment());
    },
    onGenerateReportClick() {
      this.actionType = "generateReport";
      this.showDialog = true;
    },
    generateReport() {
      this.showDialog = false;
      // Wait for closing dialog animation
      setTimeout(() => {
        // In case cordova use appropriate plugin
        if (process.env.BASE_URL === "") {
          this.$store.commit("setPrintPage", true);
          // Set default pdf name
          window.cordova.plugins.printer.setDefaults({
            name: "EASI-score-report",
            orientation: "landscape",
            pageCount: 1
          });
          window.cordova.plugins.printer.print();
          setTimeout(() => {
            this.$store.commit("setPrintPage", false);
          }, 2000);
        } else {
          window.print();
        }
      }, 100);
    },
    onConfirmClick() {
      this.$refs.evaluationForm.validate();
      if (this.isFormValid) {
        if (this.generateReportAction) {
          this.generateReport();
        } else {
          this.addEvaluation();
        }
      }
    },
    async addEvaluation() {
      try {
        this.loading = true;
        await this.$_checkTokenExpiration();
        await this.$http.put(`patients/${this.patientId}/form/${this.formId}`, {
          form: {
            body_parts: this.bodyParts,
            evaluated_on: moment(this.evaluationDate).format("YYYY-MM-DD")
          }
        });
        await this.$router.push({
          name: "pacienti",
          params: { add: "evaluation" }
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchPatient() {
      await this.$_checkTokenExpiration();
      ({
        body: { patient: this.patient, forms: this.forms }
      } = await this.$http.get(`patients/${this.patientId}`));
      this.evaluatedDate = this.formatDate(this.forms[0].evaluated_on);
      this.editedForm = this.forms.find(form => form.id == this.formId);
      const birthDate = moment(this.patient.birthdate, '"YYYY-MM-DD"');
      const age = moment().diff(birthDate, "years", false);
      if (age <= 7) {
        this.multiplier = [0.2, 0.2, 0.3, 0.3];
      } else {
        this.multiplier = [0.1, 0.2, 0.3, 0.4];
      }
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.date-picker-wrapper {
  position: absolute;
  top: 50px;
  left: 0;
  z-index: 100;
}

::v-deep .v-dialog {
  overflow-y: inherit;
  .v-card {
    background-color: #f5f5f5;
  }
}

#patientInfoBox {
  padding: 2em;
  background-color: white;
  .v-input--selection-controls.v-input {
    margin-top: 5px;
  }
}

#patientInfo {
  margin: 0 auto 2em auto;
}
#ad {
  height: 100%;
}
#printPDF {
  display: none;
}

#patient-ribbon {
  background: #f5f5f5;
  padding: 1rem 3rem;
  width: 25rem;
  margin-bottom: 1rem;
  margin-left: -2rem;

  .v-subheader {
    padding: 0;
    display: block;
    height: auto;
  }
}

.container {
  @media only screen and (min-width: 1264px) and (max-width: 1700px) {
    max-width: 1500px;
  }
}
.v-content {
  margin: 0 5em;
}

//Menu styles
.fas {
  margin-right: 1em;
  color: #324c8d;
}
.v-list-item__title.mennuHeader {
  padding: 5px 0;
  font-size: 1em;
  color: #324c8d;
}
.v-toolbar__title {
  a {
    color: white;
    text-decoration: none;
  }
}

@media screen and (max-width: 600px) {
  #patient-ribbon {
    width: 100%;
    margin: 0;
    padding: 2rem;

    h2 {
      text-align: left;
    }
  }
}
</style>
